import React, { useEffect, useCallback, useState } from "react";
import './styles.css';
import Alert from "../components/alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import PasswordChecklist from 'react-password-checklist';
import ResetPasswordAPI from "../api/resetPasswordAPI";

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [token, setToken] = useState<string>('');
    const [showPass1, setShowPass1] = useState<boolean>(false);
    const [showPass2, setShowPass2] = useState<boolean>(false);
    const [showChecklist, setShowChecklist] = useState(false);
    const [showChecklistMatch, setShowChecklistMatch] = useState(false);
    const [status, setStatus] = useState<boolean>(false);
    const [errorCode, setErrorCode] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");

        if (!token) {
            // redirect to login page if token not in URL
			window.location.href = "/";
        } else {
            setToken(token);
        }
    }, []);

    const setError = useCallback((code: number, message: string) => {
        setErrorCode(code);
        setErrorMessage(message);
    }, []);

    const handleClose = useCallback(() => {
        setErrorCode(null);
        setErrorMessage('');
    }, []);

    const handleCancel = () => {
        window.location.href = "/";
    }

    const handleSetMatchPassword = async () => {
        if (newPassword === confirmPassword) {
            try {
                let res = await ResetPasswordAPI(token, newPassword);
                if (res === 'Success') {
                    setError(0, "Password updated successfully");
                    setStatus(true);
                } else {
                     setError(1, "Failure in updating the password");
                }
            } catch (e) {
                console.log(e);
                setError(1, "Error");
            }
        } else {
            setError(1, "Passwords must match");
        }
    }

    return (
        <div className="App flex flex-col">
            <div className="flex justify-center items-center">
                {status ? (
                    // Return to Login Page
                    <div>
                        <button className="button-6" onClick={handleCancel}>Return to Dashboard</button>
                    </div>
                ) : (
                    <div className="bgShadow flex flex-col justify-center p-10 w-[40%] items-center gap-6">
                        <p className='text-3xl headerText'>Reset your password</p>

                        {/* Enter New Password */}
                        <div className="grid grid-cols-[2fr_3fr_.25fr] items-center w-full">
                            <label className='sampleLabel px-3'>New Password:</label>
                            <div className="flex justify-center">
                                <input type={showPass1 ? "text" : "password"}
                                    value={newPassword}
                                    placeholder='New password'
                                    required
                                    className='w-full border rounded px-3 py-2 textInput'
                                    onFocus={() => setShowChecklist(true)}
                                    onBlur={() => setShowChecklist(false)}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value)
                                    }} /> &nbsp;&nbsp;
                            </div>
                            <FontAwesomeIcon className="icon" icon={showPass1 ? faEyeSlash : faEye} size='sm' onClick={() => { setShowPass1(!showPass1) }} />
                        </div>

                        {/* Repeat New Password */}
                        <div className="grid grid-cols-[2fr_3fr_.25fr] items-center w-full">
                            <label className='sampleLabel px-3'>Confirm Password:</label>
                            <div className="flex justify-center">
                                <input type={showPass2 ? "text" : "password"}
                                    value={confirmPassword}
                                    placeholder='Confirm password'
                                    required
                                    className='w-full border rounded px-3 py-2 textInput'
                                    onFocus={() => setShowChecklistMatch(true)}
                                    onBlur={() => setShowChecklistMatch(false)}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                    }} /> &nbsp;&nbsp;
                            </div>
                            <div className="flex justify-left">
                                <FontAwesomeIcon className="icon" icon={showPass2 ? faEyeSlash : faEye} size='sm' onClick={() => { setShowPass2(!showPass2) }} />
                            </div>
                        </div>

                        {/* New Password Validator */}
                        {showChecklist && (
                            <div className="relative flex flex-col mt-6 text-gray-700 bg-white shadow-md bg-clip-border rounded-xl w-96">
                                <div className="p-6">
                                    <h5 className="block mb-2 font-sans text-xl antialiased font-semibold leading-snug tracking-normal text-blue-gray-900">
                                        Password Check
                                    </h5>
                                    <PasswordChecklist
                                        rules={["minLength", "specialChar", "number", "capital", "maxLength", "lowercase", "letter"]}
                                        minLength={8}
                                        maxLength={20}
                                        value={newPassword}
                                        style={{ fontFamily: 'Montaga', fontSize: '12px' }}
                                        iconSize={12}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Repeat Password Validator */}
                        {showChecklistMatch && (
                            <div className="relative flex flex-col mt-6 text-gray-700 bg-white shadow-md bg-clip-border rounded-xl w-96">
                                <div className="p-6">
                                    <h5 className="block mb-2 font-sans text-xl antialiased font-semibold leading-snug tracking-normal text-blue-gray-900">
                                        Password Check
                                    </h5>
                                    <PasswordChecklist
                                        rules={["minLength", "specialChar", "number", "capital", "maxLength", "lowercase", "letter", "match"]}
                                        minLength={8}
                                        maxLength={20}
                                        value={newPassword}
                                        valueAgain={confirmPassword}
                                        style={{ fontFamily: 'Montaga', fontSize: '12px' }}
                                        iconSize={12}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Submit */}
                        <div className='flex justify-around items-center mt-2'>
                            <div>
                                <button className="button-6" onClick={handleSetMatchPassword}>Submit</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {errorCode !== null ? (
                <div className="absolute top-0 right-0">
                    <Alert 
                        code={errorCode} 
                        message={errorMessage} 
                        onClose={handleClose} 
                    />
                </div>
            ) : null}
        </div>
    );
} 

export default ResetPassword;